import type { CyclingStandings, Fixture, RiderRef } from '@h2h/shared';

export type RiderVanityMeta = {
  teamName: string | null;
  nationality: string | null;
};

/** riderUrl → team/nationality from race standings tables (GC, jerseys, …). */
export function riderMetaFromStandings(standings: CyclingStandings | null | undefined): Map<string, RiderVanityMeta> {
  const map = new Map<string, RiderVanityMeta>();
  if (!standings) return map;
  for (const cls of standings.classifications ?? []) {
    for (const row of cls.rows) {
      if (!row.riderUrl) continue;
      const teamName = row.teamName?.trim() || null;
      const nationality = row.nationality?.trim() || null;
      if (!teamName && !nationality) continue;
      const prev = map.get(row.riderUrl);
      map.set(row.riderUrl, {
        teamName: prev?.teamName ?? teamName,
        nationality: prev?.nationality ?? nationality
      });
    }
  }
  return map;
}

/** riderUrl → team/nationality from any fixture in the batch that already carries them. */
export function riderMetaFromFixtures(fixtures: readonly Fixture[] | null | undefined): Map<string, RiderVanityMeta> {
  const map = new Map<string, RiderVanityMeta>();
  for (const f of fixtures ?? []) {
    for (const ref of [f.riderA, f.riderB]) {
      if (!ref?.riderUrl) continue;
      const teamName = ref.teamName?.trim() || null;
      const nationality = ref.nationality?.trim() || null;
      if (!teamName && !nationality) continue;
      const prev = map.get(ref.riderUrl);
      map.set(ref.riderUrl, {
        teamName: prev?.teamName ?? teamName,
        nationality: prev?.nationality ?? nationality
      });
    }
  }
  return map;
}

export function mergeRiderMetaMaps(...maps: readonly Map<string, RiderVanityMeta>[]): Map<string, RiderVanityMeta> {
  const out = new Map<string, RiderVanityMeta>();
  for (const m of maps) {
    for (const [url, meta] of m) {
      const prev = out.get(url);
      out.set(url, {
        teamName: prev?.teamName ?? meta.teamName,
        nationality: prev?.nationality ?? meta.nationality
      });
    }
  }
  return out;
}

export function enrichRiderRef(ref: RiderRef | undefined, meta: Map<string, RiderVanityMeta>): RiderRef | undefined {
  if (!ref) return ref;
  const patch = ref.riderUrl ? meta.get(ref.riderUrl) : undefined;
  if (!patch) return ref;
  return {
    ...ref,
    teamName: ref.teamName?.trim() || patch.teamName,
    nationality: ref.nationality?.trim() || patch.nationality
  };
}

/** Stamp missing team/nationality onto cycling duel fixtures for display. */
export function enrichCyclingFixture(fixture: Fixture, meta: Map<string, RiderVanityMeta>): Fixture {
  if (!fixture.riderA && !fixture.riderB) return fixture;
  return {
    ...fixture,
    riderA: enrichRiderRef(fixture.riderA, meta),
    riderB: enrichRiderRef(fixture.riderB, meta)
  };
}

export function enrichCyclingFixtures(fixtures: readonly Fixture[], meta: Map<string, RiderVanityMeta>): Fixture[] {
  return fixtures.map((f) => enrichCyclingFixture(f, meta));
}
